import type {
  GeoJsonFeatureCollection,
  LatLngBounds,
  MapMarker,
} from '@gpx-spa/geo-domain';
import { latLngBoundsFromLngLatCoordinates } from '@gpx-spa/geo-domain';
import {
  SHIPMENT_STOP_ROLE_VISUAL,
  shipmentStopRoleFromIndex,
} from '@gpx-spa/constants';
import type { V2ShipmentStop } from '@gpx-spa/queries/src/getAccountShipmentV2';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Map marker position — API always sends `center` on each stop. */
export function lngLatForShipmentStopMarker(stop: V2ShipmentStop): {
  lat: number;
  lng: number;
} {
  return { lat: stop.center.lat, lng: stop.center.lng };
}

/**
 * Renders each stop’s `geojson` Feature with role-based fill/stroke (same palette as shipment create).
 */
export function v2ShipmentStopsToBoundariesFeatureCollection(
  stops: V2ShipmentStop[],
): GeoJsonFeatureCollection | null {
  const sorted = [...stops].sort((a, b) => a.sequence - b.sequence);
  const total = sorted.length;
  const out: GeoJsonFeatureCollection['features'] = [];

  for (let i = 0; i < total; i++) {
    const stop = sorted[i]!;
    const gj = stop.geojson;
    if (
      gj == null ||
      typeof gj !== 'object' ||
      gj.geometry == null ||
      typeof gj.geometry !== 'object'
    ) {
      continue;
    }
    const role = shipmentStopRoleFromIndex(i, total);
    const m = SHIPMENT_STOP_ROLE_VISUAL[role].map;
    const label = stop.name?.trim() || `Stop ${i + 1}`;
    const baseProps = gj.properties ?? {};
    out.push({
      type: 'Feature',
      geometry: gj.geometry,
      properties: {
        ...baseProps,
        map: {
          fill: m.fill,
          fillOpacity: m.fillOpacity,
          stroke: m.stroke,
          strokeOpacity: m.strokeOpacity,
          strokeWidth: m.strokeWidth,
          popup: escapeHtml(label),
        },
      },
    });
  }

  if (!out.length) return null;
  return { type: 'FeatureCollection', features: out };
}

/**
 * Numbered circle markers (1…n) — same order as `sequence`, colors by origin/waypoint/destination.
 */
export function v2ShipmentStopsToNumberedMarkers(
  stops: V2ShipmentStop[],
): MapMarker[] {
  const sorted = [...stops].sort((a, b) => a.sequence - b.sequence);
  const total = sorted.length;
  if (total === 0) return [];

  const out: MapMarker[] = [];
  for (let i = 0; i < total; i++) {
    const stop = sorted[i]!;
    const c = stop.center;
    if (
      c == null ||
      typeof c.lat !== 'number' ||
      typeof c.lng !== 'number' ||
      !Number.isFinite(c.lat) ||
      !Number.isFinite(c.lng)
    ) {
      continue;
    }
    const role = shipmentStopRoleFromIndex(i, total);
    const fill = fillHexForRole(role);
    const name = stop.name?.trim() || `Stop ${i + 1}`;
    const popup = `<div class="text-[10px]"><div class="font-medium">${escapeHtml(name)}</div><div>Seq. ${stop.sequence}</div></div>`;
    const pos = lngLatForShipmentStopMarker(stop);
    out.push({
      id: `v2-stop-${stop.id ?? i}-${stop.sequence}`,
      position: pos,
      style: {
        kind: 'circle',
        fillColor: fill,
        borderColor: '#ffffff',
        label: String(i + 1),
        sizePx: 32,
        fontSizePx: 14,
        textColor: '#ffffff',
      },
      popup,
      popupHtml: true,
    });
  }
  return out;
}

function fillHexForRole(
  role: ReturnType<typeof shipmentStopRoleFromIndex>,
): string {
  if (role === 'origin') return '#22c55e';
  if (role === 'waypoint') return '#3b82f6';
  return '#334155';
}

/** Bounds that include all stop boundary rings (for fitBounds). */
export function latLngBoundsFromStopsGeojson(
  stops: V2ShipmentStop[],
): LatLngBounds | null {
  const fc = v2ShipmentStopsToBoundariesFeatureCollection(stops);
  if (!fc?.features.length) return null;
  const coords: [number, number][] = [];
  for (const f of fc.features) {
    const g = f.geometry;
    if (g.type === 'Polygon' && Array.isArray(g.coordinates?.[0])) {
      for (const c of g.coordinates[0] as [number, number][]) {
        if (c.length >= 2) coords.push([c[0], c[1]]);
      }
    }
    if (g.type === 'MultiPolygon' && Array.isArray(g.coordinates?.[0]?.[0])) {
      for (const c of g.coordinates[0][0] as [number, number][]) {
        if (c.length >= 2) coords.push([c[0], c[1]]);
      }
    }
  }
  return latLngBoundsFromLngLatCoordinates(coords);
}
